import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "../router";
import type { User } from "../types";
import { IconChat, IconDoc, IconMemory, IconShield } from "./Icons";

export type Section = "chat" | "documents" | "compliance" | "memory";

const NAV: { id: Section; to: string; label: string; icon: ReactNode }[] = [
  { id: "chat", to: "/chat", label: "Cercetare", icon: <IconChat /> },
  { id: "documents", to: "/documents", label: "Documente", icon: <IconDoc /> },
  { id: "compliance", to: "/compliance", label: "Conformitate", icon: <IconShield /> },
  { id: "memory", to: "/memory", label: "Memorie", icon: <IconMemory /> },
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "");
}

/**
 * The frame every screen sits in: the sidebar with the four sections, a
 * slot for the section's own list (conversations, on Cercetare), and the
 * account at the bottom.
 *
 * Below 860px the sidebar becomes a wrapping bar across the top and the
 * secondary list hides; Cercetare offers its list from its own top bar.
 */
export function Shell({
  user,
  section,
  secondary,
  onLogout,
  children,
}: {
  user: User;
  section: Section;
  secondary?: ReactNode;
  onLogout: () => void;
  children: ReactNode;
}) {
  return (
    <div className="shell">
      <nav className="side" aria-label="Navigare principală">
        <Link to="/chat" className="brand">
          <span className="logo" aria-hidden="true">a</span>
          arhilex
          <span className="pilot">pilot</span>
        </Link>
        {NAV.map((n) => (
          <Link
            key={n.id}
            to={n.to}
            className={n.id === section ? "nav on" : "nav"}
            aria-current={n.id === section ? "page" : undefined}
          >
            {n.icon}
            {n.label}
          </Link>
        ))}
        <div className="side-sec">{secondary}</div>
        <Account user={user} onLogout={onLogout} />
      </nav>
      <main className="content">{children}</main>
    </div>
  );
}

function Account({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Closes on any click outside, like every other menu - no modal, nothing
  // to dismiss.
  useEffect(() => {
    if (!open) return;
    function away(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function esc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className="me" ref={ref}>
      <button
        className="me-btn"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar" aria-hidden="true">
          {initials(user.name).toUpperCase() || "·"}
        </span>
        <span className="me-name">Contul meu</span>
      </button>
      {open && (
        <div className="me-menu" role="menu">
          <div className="me-who">
            <strong>{user.name}</strong>
            <span>{user.email}</span>
          </div>
          <button role="menuitem" onClick={onLogout}>
            Ieșire
          </button>
        </div>
      )}
    </div>
  );
}
